import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Collapsible from 'react-collapsible';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import * as referenceDataActions from '../../redux/actions/referenceDataActions';
import * as translationActions from '../../redux/actions/translationActions';

import {
  smartPegCompatibleSelector,
  abutmentCompatibleSelector,
} from '../../redux/reducers/referenceDataReducer';

import 'whatwg-fetch';

import styles from './SmartPegTool.module.scss';

import smartpegSingleImgLow from './local-assets/smartpeg-single_low.png';
import smartpegReusableImgLow from './local-assets/smartpeg-reusable_low.png';
import { Button } from '../Shared/Buttons';

const SEARCH_TYPES = {
  FREE: 'free',
  IMPLANT: 'implant',
  ABUTMENT: 'abutment',
  SMARTPEG: 'smartpeg',
};

const NAVIGATION_STRINGS = {
  LIST_ARTICLES: 'list-articles',
  MODEL_LIST: 'model-list',
  SINGLE_SMARTPEG: 'single-smartpeg',
};

const RESULT_LENGTH = 40;

class SmartPegTool extends Component {
  constructor(props) {
    super(props);

    this.state = {
      filterList: [],
      resultList: null,
      searchType: SEARCH_TYPES.FREE,
      navigation: SEARCH_TYPES.FREE,
      navigationData: null,
      history: [],
      lastSearchString: '',
      downloadingList: false,
    };

    this.searchChanged = this.searchChanged.bind(this);
    this.downloadList = this.downloadList.bind(this);
  }

  componentDidMount() {
    this.props.actions.getImplantData(true, this.props.draft);
    this.props.actions.getAbutmentData(true, this.props.draft);
    this.props.actions.getSmartPegData();
  }

  downloadList(brandName) {
    this.setState({
      downloadingList: true,
    });
    this.props.actions.getSmartPegListForBrand(brandName, () =>
      this.setState({
        downloadingList: false,
      })
    );
  }

  changeLocale(event) {
    this.props.actions.changeLocale(event.target.value);
  }

  searchChanged(evt) {
    let result = [];
    const text = evt.target.value;

    if (text.length < 1) {
      this.setState({
        resultList: null,
        lastSearchString: text,
      });
      return;
    }

    const filteredImplants = this.props.implants.filter((imp) => imp.status === 0);
    const filteredAbutments = this.props.abutments.filter((ab) => ab.status === 0);
    let tempList;

    switch (this.state.searchType) {
      case SEARCH_TYPES.FREE:
        tempList = filteredImplants;
        break;
      case SEARCH_TYPES.IMPLANT:
        tempList = filteredImplants;
        break;
      case SEARCH_TYPES.ABUTMENT:
        tempList = filteredAbutments;
        break;
      case SEARCH_TYPES.SMARTPEG:
        tempList = this.props.smartPegs;
        result = tempList.filter((smartPeg) => matchesSmartPeg(smartPeg, text));

        this.setState({
          resultList: result,
          lastSearchString: text,
        });
        // NOTE early return!
        return;
      default:
        tempList = filteredImplants;
        break;
    }

    result = tempList.filter((item) => {
      let match = false;
      if (item.brandName?.toLowerCase().includes(text.toLowerCase())) match = true;

      if (item.modelName?.toLowerCase().includes(text.toLowerCase())) match = true;

      if (item.platformName?.toLowerCase().includes(text.toLowerCase())) match = true;

      if (item.partNumber?.toLowerCase().includes(text.toLowerCase())) match = true;

      return match;
    });

    this.setState({
      resultList: result,
      lastSearchString: text,
    });
  }

  resetSearch() {
    this.setState({
      searchType: SEARCH_TYPES.FREE,
      navigation: SEARCH_TYPES.FREE,
      resultList: null,
      lastSearchString: '',
      history: [],
    });
  }

  setCurrentSearch(searchType) {
    this.setState({
      searchType,
      resultList: null,
      lastSearchString: '',
    });
  }

  navigate(navString, data) {
    this.setState({
      navigation: navString,
      navigationData: data,
      history: this.state.history.concat({
        navigation: this.state.navigation,
        navigationData: this.state.navigationData,
      }),
    });
  }

  navigateBack() {
    const newHistory = this.state.history.concat([]);
    const lastHistory = newHistory.pop();

    this.setState({
      navigation: lastHistory.navigation,
      navigationData: lastHistory.navigationData,
      history: newHistory,
    });
  }

  dataLoaded() {
    return (
      this.props.implants &&
      this.props.implants.length > 0 &&
      this.props.abutments &&
      this.props.abutments.length > 0 &&
      this.props.smartPegs &&
      this.props.smartPegs.length > 0
    );
  }

  landingResults() {
    return (
      <ParentComponent>
        <div className={styles.landingPageRoot}>
          <div>
            <input
              className={styles.searchBox}
              onChange={this.searchChanged}
              type="text"
              placeholder="Search"
              defaultValue={this.state.lastSearchString}
            />
          </div>
          {this.state.resultList ? (
            <div>
              <div className={styles.resultListWrapper}>
                {(this.state.resultList || []).slice(0, RESULT_LENGTH).map((val) => {
                  return (
                    <ArticleItem
                      link
                      key={val.dataId}
                      data={val}
                      navigate={(navString, data) => {
                        this.navigate(navString, data);
                      }}
                    />
                  );
                })}
              </div>
            </div>
          ) : (
            <div>
              <div>
                <div className={styles.divider}>OR</div>
              </div>
              <div>
                <h2 className={styles.separatorHeader}>Find by</h2>
                <div className={styles.iconButtonWrapper}>
                  <div
                    className={styles.iconButton}
                    onClick={() => {
                      this.setCurrentSearch(SEARCH_TYPES.IMPLANT);
                      this.navigate(SEARCH_TYPES.IMPLANT);
                    }}
                  >
                    <i className={styles.itemIcon}>implant</i>
                    <label className={styles.itemIconLabel}>Implant</label>
                  </div>
                  <div
                    className={styles.iconButton}
                    onClick={() => {
                      this.setCurrentSearch(SEARCH_TYPES.ABUTMENT);
                      this.navigate(SEARCH_TYPES.ABUTMENT);
                    }}
                  >
                    <i className={styles.itemIcon}>abutment</i>
                    <label className={styles.itemIconLabel}>Abutment</label>
                  </div>
                  <div
                    className={styles.iconButton}
                    onClick={() => {
                      this.setCurrentSearch(SEARCH_TYPES.SMARTPEG);
                      this.navigate(SEARCH_TYPES.SMARTPEG);
                    }}
                  >
                    <i className={styles.itemIcon}>smartpeg</i>
                    <label className={styles.itemIconLabel}>SmartPeg</label>
                  </div>
                </div>
              </div>
              <div>
                <div className={styles.divider}>OR</div>
              </div>
              <Button
                className={styles.downloadsmartpegguide}
                centered
                value={'Download SmartPeg Reference Guide'}
                onClick={() => {
                  const newWindow = window.open(
                    'https://www.osstell.com/app/uploads/RefList_25016-93_April_2026-1.pdf',
                    '_blank',
                    'noopener,noreferrer'
                  );
                  if (newWindow) newWindow.opener = null;
                }}
                primary
              />
            </div>
          )}
        </div>
      </ParentComponent>
    );
  }

  abutmentImplantResults(key) {
    const dataToFilterOn = this.state.lastSearchString ? this.state.resultList : this.props[key];

    const resultsByBrandName = Object.keys(
      (dataToFilterOn || []).reduce((memo, next) => {
        memo[next.brandName] = '';
        return memo;
      }, {})
    );
    resultsByBrandName.sort((a, b) => a.localeCompare(b));

    const backButton = (
      <i
        className={styles.backIcon}
        onClick={() => {
          this.resetSearch();
        }}
      >
        back
      </i>
    );

    const title = { implants: 'Implant', abutments: 'Abutment' }[key];

    return (
      <ParentComponent title={`Find by ${title}`} backButton={backButton}>
        <div>
          <div className={styles.maxWidthWrapper}>
            <div>
              <input
                onChange={this.searchChanged}
                className={styles.searchBox}
                type="text"
                placeholder="Search"
                defaultValue={this.state.lastSearchString}
              />
            </div>
            <div>
              <ul className={styles.resultListWrapper}>
                {(resultsByBrandName || []).map((brandName, index) => {
                  return (
                    <li
                      className={styles.brandNameItem}
                      key={brandName + index}
                      onClick={() => {
                        this.navigate(NAVIGATION_STRINGS.MODEL_LIST, {
                          brandName,
                          propKey: key,
                        });
                      }}
                    >
                      {brandName}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </ParentComponent>
    );
  }

  smartPegResults() {
    const dataToFilterOn = (
      this.state.lastSearchString ? this.state.resultList : this.props.smartPegs
    ).concat([]);

    dataToFilterOn.sort((a, b) => a.index - b.index);

    const backButton = (
      <i
        className={styles.backIcon}
        onClick={() => {
          this.resetSearch();
        }}
      >
        back
      </i>
    );

    return (
      <ParentComponent backButton={backButton}>
        <div>
          <div className={styles.maxWidthWrapper}>
            <div>
              <input
                className={styles.searchBox}
                onChange={this.searchChanged}
                type="text"
                placeholder="Search"
                defaultValue={this.state.lastSearchString}
              />
            </div>
            <ul className={styles.smartPegListWrapper}>
              {dataToFilterOn.map((smartPeg, index) => {
                const hasSingleUse = smartPeg.type?.trim() && smartPeg.partNumber?.trim();
                const hasReusable =
                  smartPeg.reusableType?.trim() && smartPeg.reusablePartNumber?.trim();

                return (
                  <li
                    key={
                      smartPeg.type ??
                      smartPeg.partNumber ??
                      smartPeg.reusablePartNumber ??
                      crypto.randomUUID()
                    }
                    className={styles.smartPegListItem}
                    onClick={() => {
                      const su = smartPeg.type?.trim();
                      const ru = smartPeg.reusableType?.trim();
                      const chosenType =
                        (su && su.length > 0 ? su : null) || (ru && ru.length > 0 ? ru : null);
                      if (!chosenType) return;
                      this.navigate(NAVIGATION_STRINGS.SINGLE_SMARTPEG, { type: chosenType });
                    }}
                  >
                    <div className={styles.smartPegListContent}>
                      <div className={styles.smartPegTextRow}>
                        {hasSingleUse && (
                          <div className={styles.smartPegTextBlock}>
                            <img
                              className={styles.smartPegListImg}
                              src={smartpegSingleImgLow}
                              alt=""
                              aria-hidden="true"
                            />
                            <div className={styles.smartPegListSectionTitle}>Single-use</div>
                            <div className={styles.smartPegListType}>{smartPeg.type}</div>
                            <div className={styles.smartPegListRef}>REF {smartPeg.partNumber}</div>
                          </div>
                        )}

                        {hasReusable && (
                          <div className={styles.smartPegTextBlock}>
                            <img
                              className={styles.smartPegListImg}
                              src={smartpegReusableImgLow}
                              alt=""
                              aria-hidden="true"
                            />
                            <div className={styles.smartPegListSectionTitle}>Reusable</div>
                            <div className={styles.smartPegListType}>{smartPeg.reusableType}</div>
                            <div className={styles.smartPegListRef}>
                              REF {smartPeg.reusablePartNumber}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </ParentComponent>
    );
  }

  modelList() {
    const propKey = this.state.navigationData.propKey;
    const brandName = this.state.navigationData.brandName;
    const resultFilteredByBrandName = this.props[propKey].filter((val) => {
      return val.brandName === brandName && val.status === 0;
    });

    const uniqueModels = Object.keys(
      resultFilteredByBrandName.reduce((memo, next) => {
        memo[next.modelName] = '';
        return memo;
      }, {})
    );
    uniqueModels.sort((a, b) => a.localeCompare(b));

    const backButton = (
      <i
        className={styles.backIcon}
        onClick={() => {
          this.navigateBack();
        }}
      >
        back
      </i>
    );

    return (
      <ParentComponent title={brandName} backButton={backButton}>
        <div>
          <ul className={styles.modelSearchResults}>
            {uniqueModels.map((modelName, index) => {
              return (
                <li
                  onClick={() => {
                    this.navigate(NAVIGATION_STRINGS.LIST_ARTICLES, {
                      brandName,
                      filterKey: modelName,
                      propKey,
                      dataKey: 'modelName',
                    });
                  }}
                  className={styles.modelSearchResultsItem}
                  key={modelName + index}
                >
                  <span className={styles.modelSearchItemText}>{modelName}</span>
                </li>
              );
            })}
          </ul>
          <Button
            primary
            value={`Download SmartPeg list for ${brandName}`}
            onClick={() => this.downloadList(brandName)}
            loading={this.state.downloadingList}
            disabled={this.state.downloadingList}
          />
        </div>
      </ParentComponent>
    );
  }

  listArticles() {
    const brandNameKey = this.state.navigationData.brandName;
    const propKey = this.state.navigationData.propKey;
    const dataKey = this.state.navigationData.dataKey;
    const filterKey = this.state.navigationData.filterKey;

    const resultFilteredByBrandName = this.props[propKey].filter((val) => {
      return val['brandName'] === brandNameKey && val[dataKey] === filterKey && val.status === 0;
    });

    const backButton = (
      <i
        className={styles.backIcon}
        onClick={() => {
          this.navigateBack();
        }}
      >
        back
      </i>
    );

    const brandName = resultFilteredByBrandName[0]?.brandName
      ? resultFilteredByBrandName[0]?.brandName
      : '';

    const smartPegs = (resultFilteredByBrandName || []).slice(0, RESULT_LENGTH);

    return (
      <ParentComponent title={`${brandName} - ${filterKey}`} backButton={backButton}>
        <div>
          <div className={styles.resultListWrapper}>
            {this.sortSmartPegs(smartPegs).map((val) => {
              return (
                <ArticleItem
                  link
                  key={val.dataId}
                  data={val}
                  navigate={(navString, data) => {
                    this.navigate(navString, data);
                  }}
                />
              );
            })}
          </div>
        </div>
      </ParentComponent>
    );
  }

  renderSingleSmartPeg() {
    const backButton = (
      <i
        className={styles.backIcon}
        onClick={() => {
          this.navigateBack();
        }}
      >
        back
      </i>
    );

    const normalizedPeg = selectSmartPegByType(
      this.props.smartPegs,
      this.state.navigationData.type
    );

    if (!normalizedPeg) {
      return (
        <ParentComponent backButton={backButton}>
          <div className={styles.singleSmartPegWrapper}>
            <div className={styles.notAvailable}>SmartPeg not available</div>
          </div>
        </ParentComponent>
      );
    }

    const smartPeg = normalizedPeg.original;

    const hasSingleUse = !!(smartPeg.type?.trim() && smartPeg.partNumber?.trim());
    const hasReusable = !!(smartPeg.reusableType?.trim() && smartPeg.reusablePartNumber?.trim());

    const compatType = hasSingleUse
      ? normalizedPeg.type
      : hasReusable
      ? smartPeg.reusableType.trim()
      : normalizedPeg.type; // final fallback (shouldn't hit in practice)

    const compatibleImplants = smartPegCompatibleSelector(this.props.implants, compatType);
    const compatibleAbutments = abutmentCompatibleSelector(this.props.abutments, compatType);

    const CollapsibleTriggerHeader = (string, open) => {
      return (
        <div className={styles.collapsibleHeader}>
          {string} <i className={styles.icon}>{open ? 'arrow_down' : 'arrow_right'}</i>
        </div>
      );
    };
    const CompatibleComponent = (props) => {
      const { brands, title } = props;
      return (
        <div className={styles.compatibleWrapper}>
          <Collapsible
            trigger={CollapsibleTriggerHeader(title, false)}
            triggerWhenOpen={CollapsibleTriggerHeader(title, true)}
          >
            {(brands || []).map((brand) => {
              return (
                <Collapsible
                  trigger={brand.brandName}
                  key={brand.brandName ?? brand.id ?? crypto.randomUUID()}
                  classParentString={styles.collapsible}
                  className={styles.collapsible}
                  openedClassName={styles.collapsibleOpen}
                  triggerClassName={styles.trigger}
                  triggerOpenedClassName={styles.triggerOpen}
                  transitionTime={200}
                >
                  {(brand.models || []).map((model) => (
                    <Collapsible
                      trigger={model.modelName}
                      key={model.modelName ?? model.id ?? crypto.randomUUID()}
                      classParentString={styles.collapsibleInner}
                      className={styles.collapsibleInner}
                      openedClassName={styles.collapsibleOpen}
                      triggerClassName={styles.trigger}
                      triggerOpenedClassName={styles.triggerOpen}
                    >
                      <div className={styles.resultListWrapper}>
                        {(model.implants || model.abutments || []).map((implant) => (
                          <ArticleItem
                            key={implant.dataId ?? implant.id ?? crypto.randomUUID()}
                            data={implant}
                          />
                        ))}
                      </div>
                    </Collapsible>
                  ))}
                </Collapsible>
              );
            })}
          </Collapsible>
        </div>
      );
    };

    return (
      <ParentComponent backButton={backButton}>
        <div>
          <div className={styles.singleSmartPegWrapper}>
            <table className={styles.smartPegTable}>
              <thead>
                <tr>
                  <td>
                    <img
                      className={`${styles.smartPegImgBig} ${
                        smartPeg.type && smartPeg.partNumber ? '' : styles.disabledImage
                      }`}
                      src={smartpegSingleImgLow}
                      alt=""
                    />
                  </td>
                  <td>
                    <img
                      className={`${styles.smartPegImgBig} ${
                        smartPeg.reusableType && smartPeg.reusablePartNumber
                          ? ''
                          : styles.disabledImage
                      }`}
                      src={smartpegReusableImgLow}
                      alt=""
                    />
                  </td>
                </tr>
                <tr>
                  <th className={styles.headerCell}>Single-use</th>
                  <th className={styles.headerCell}>Reusable</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={styles.cell}>
                    {smartPeg.type && smartPeg.partNumber ? (
                      <>
                        <div className={styles.type}>{smartPeg.type}</div>
                        <div className={styles.ref}>REF {smartPeg.partNumber}</div>
                      </>
                    ) : (
                      <div className={styles.notAvailable}>Not available</div>
                    )}
                  </td>
                  <td className={styles.cell}>
                    {smartPeg.reusableType && smartPeg.reusablePartNumber ? (
                      <>
                        <div className={styles.type}>{smartPeg.reusableType}</div>
                        <div className={styles.ref}>REF {smartPeg.reusablePartNumber}</div>
                      </>
                    ) : (
                      <div className={styles.notAvailable}>Not available</div>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div>
            <h2 className={styles.separatorHeader}>SmartPeg Type also suitable for</h2>

            {compatibleImplants.brands && compatibleImplants.brands.length > 0 && (
              <CompatibleComponent title="Implants" brands={compatibleImplants.brands} />
            )}

            {compatibleAbutments.brands && compatibleAbutments.brands.length > 0 && (
              <CompatibleComponent title="Abutments" brands={compatibleAbutments.brands} />
            )}
          </div>
        </div>
      </ParentComponent>
    );
  }

  navSwitch() {
    switch (this.state.navigation) {
      case SEARCH_TYPES.FREE:
        return this.landingResults();
      case SEARCH_TYPES.IMPLANT:
        return this.abutmentImplantResults('implants');
      case SEARCH_TYPES.ABUTMENT:
        return this.abutmentImplantResults('abutments');
      case SEARCH_TYPES.SMARTPEG:
        return this.smartPegResults();
      case NAVIGATION_STRINGS.MODEL_LIST:
        return this.modelList();
      case NAVIGATION_STRINGS.LIST_ARTICLES:
        return this.listArticles();
      case NAVIGATION_STRINGS.SINGLE_SMARTPEG:
        return this.renderSingleSmartPeg();
      default:
        return this.landingResults();
    }
  }

  sortSmartPegs(smartPegs) {
    var sortedSmartPegs = smartPegs.sort(function (x, y) {
      if (x.platformName > y.platformName) {
        return 1;
      } else if (x.platformName < y.platformName) {
        return -1;
      }

      if (Number(x.diameter) < Number(y.diameter)) {
        return -1;
      } else if (Number(x.diameter) > Number(y.diameter)) {
        return 1;
      }

      if (Number(x.length) < Number(y.length)) {
        return -1;
      } else if (Number(x.length) > Number(y.length)) {
        return 1;
      } else {
        return 0;
      }
    });

    return sortedSmartPegs;
  }

  render() {
    return (
      <div className={styles.smartPegToolPanelWrapper}>
        <div className={styles.smartPegToolPanel}>
          {this.props.draft && <h1 className={styles.h1}>Draft</h1>}
          {this.dataLoaded() ? (
            this.navSwitch()
          ) : (
            <ParentComponent>
              <div className={styles.loading}> Loading...</div>{' '}
            </ParentComponent>
          )}
        </div>
      </div>
    );
  }
}

function ParentComponent(props) {
  return (
    <div>
      {props.backButton || null}
      <h1 className={styles.h1}>{props.title || 'Find your SmartPeg'}</h1>
      {props.children}
    </div>
  );
}

function ArticleItem(props) {
  const navigate = props.navigate;
  const dataPoint = (labelText, dataPoint) => {
    return (
      <div className={styles.searchResultItem}>
        <div className={styles.searchResultLabel}>{dataPoint || '-'}</div>
        <div className={styles.searchResultText}>{labelText}</div>
      </div>
    );
  };
  const data = props.data;

  const wrapperClass = classNames(
    styles.searchResultsWrapper,
    props.link === true ? styles.searchResultsLink : null,
    props.link === true ? styles.shadow : null,
    props.link === true ? null : styles.searchResultsFlat
  );
  return (
    <div
      className={wrapperClass}
      onClick={() => {
        if (!props.link) return;
        const chosenType =
          (data.smartPeg && data.smartPeg.trim()) || (data.smartPegR && data.smartPegR.trim());
        if (!chosenType) return;
        navigate &&
          navigate(NAVIGATION_STRINGS.SINGLE_SMARTPEG, {
            type: chosenType,
          });
      }}
    >
      <div className={styles.searchResultsMiddleWrapper}>
        <h3
          className={styles.searchResultHeader}
          title={`${data.brandName} / ${data.modelName}`}
        >{`${data.brandName} / ${data.modelName}`}</h3>
        <div className={styles.searchResults}>
          {dataPoint(data.platformName, 'Platform')}
          {dataPoint(data.diameter, 'Diameter')}
          {dataPoint(data.length, 'Length')}
          {dataPoint(data.partNumber, 'Implant REF')}
          {data.smartPeg && data.smartPegPartNumber ? (
            <>
              {dataPoint(data.smartPeg, 'Single-use SmartPeg')}
              {dataPoint(data.smartPegPartNumber, 'Single-use REF')}
            </>
          ) : (
            <>
              {dataPoint('Not available', 'Single-use SmartPeg')}
              {dataPoint('', ' ')}
            </>
          )}
          {data.smartPegR && data.smartPegRPartNumber ? (
            <>
              {dataPoint(data.smartPegR, 'Reusable SmartPeg')}
              {dataPoint(data.smartPegRPartNumber, 'Reusable REF')}
            </>
          ) : (
            <>{dataPoint('Not available', 'Reusable SmartPeg')}</>
          )}
        </div>
      </div>
    </div>
  );
}

function mapState(state) {
  return {
    locale: state.translation.locale,
    translations: state.translation.currentTranslations,
    implants: state.referenceData.implantData,
    abutments: state.referenceData.abutmentData,
    smartPegs: state.referenceData.smartPegData,
  };
}

function mapDispatch(dispatch) {
  return {
    actions: bindActionCreators({ ...translationActions, ...referenceDataActions }, dispatch),
  };
}

function normalizeSmartPeg(smartPeg, chosenTypeRaw) {
  const chosenType = chosenTypeRaw?.trim();
  const isSingleUse = smartPeg.type?.trim() === chosenType;
  const isReusable = smartPeg.reusableType?.trim() === chosenType;

  if (isSingleUse) {
    return {
      type: smartPeg.type?.trim(),
      partNumber: smartPeg.partNumber?.trim(),
      isReusable: false,
      original: smartPeg,
    };
  }
  if (isReusable) {
    return {
      type: smartPeg.reusableType?.trim(),
      partNumber: smartPeg.reusablePartNumber?.trim(),
      isReusable: true,
      original: smartPeg,
    };
  }
  return null;
}

function selectSmartPegByType(smartPegs, chosenTypeRaw) {
  const chosenType = chosenTypeRaw?.trim();
  if (!chosenType) return null;
  for (const peg of smartPegs) {
    const normalized = normalizeSmartPeg(peg, chosenType);
    if (normalized) return normalized;
  }
  return null;
}

const matchesSmartPeg = (peg, search) => {
  return (
    matchesSearch(peg.type, search) ||
    matchesSearch(peg.partNumber, search) ||
    matchesSearch(peg.reusableType, search) ||
    matchesSearch(peg.reusablePartNumber, search)
  );
};

const matchesSearch = (value, search) => {
  if (!value || !search) return false;

  const v = value.trim().toLowerCase();
  const s = search.trim().toLowerCase();

  // keep includes, but avoid wrong shorter matches
  if (v.length < s.length) return false;

  return v.includes(s);
};

SmartPegTool.propTypes = {
  actions: PropTypes.shape({
    getImplantData: PropTypes.func.isRequired,
    getAbutmentData: PropTypes.func.isRequired,
    getSmartPegData: PropTypes.func.isRequired,
    getSmartPegListForBrand: PropTypes.func,
    changeLocale: PropTypes.func,
  }).isRequired,

  draft: PropTypes.bool,
  implants: PropTypes.array,
  abutments: PropTypes.array,
  smartPegs: PropTypes.array,
};

ParentComponent.propTypes = {
  backButton: PropTypes.node,
  title: PropTypes.string,
  children: PropTypes.node,
};

ArticleItem.propTypes = {
  data: PropTypes.shape({
    brandName: PropTypes.string,
    modelName: PropTypes.string,
    platformName: PropTypes.string,
    diameter: PropTypes.string,
    length: PropTypes.string,
    partNumber: PropTypes.string,
    smartPeg: PropTypes.string,
    smartPegPartNumber: PropTypes.string,
    smartPegR: PropTypes.string,
    smartPegRPartNumber: PropTypes.string,
    dataId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    status: PropTypes.number,
  }).isRequired,

  navigate: PropTypes.func,
  link: PropTypes.bool,
};

export default connect(mapState, mapDispatch)(SmartPegTool);
